import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { AudioRecorder } from '../../classes/AudioRecorder';
import { TrackingEvents, sendLoggingEvents } from '../../events';
import { audioRecordingEntitiesAtom } from './state';

function createAudioRecorder({ audioStreamToRecord }: { audioStreamToRecord: MediaStream }) {
  return new AudioRecorder(
    audioStreamToRecord,
    {
      audioBitsPerSecond: 16_000,
    },
    {
      onerror: (event) => {
        sendLoggingEvents(
          TrackingEvents.AUDIO_RECORDING_ERROR,
          {
            // log any additional data we have access to
            eventError: event.error,
            eventMessage: event.message,
            eventType: event.type,
            eventTarget: event.target,
            eventColno: event.colno,
            eventFilename: event.filename,
            eventLineno: event.lineno,
          },
          {
            logLevel: 'error',
            message: 'Audio recording error',
          },
        );
      },
    },
  );
}

/**
 * Returns callbacks to manage the lifecycle of the audio recorder i.e. setting
 * up and tearing down the recording environment.
 *
 * ⚠️ Please do not use these callbacks to start and stop recording. Use the
 * `useRecordAudioCallbacks` for that instead.
 */
export function useAudioRecorderLifecycleCallbacks() {
  const getAudioRecordingEntities = useAtomCallback(useCallback((get) => get(audioRecordingEntitiesAtom), []));
  const setAudioRecordingEntities = useSetAtom(audioRecordingEntitiesAtom);

  /**
   * If not already set up, then sets up the recording environment by creating
   * an audio context, the audio stream to record, and an audio recorder. It's
   * necessary to call this before starting recording.
   *
   * It's also important that this setup is done after some user interaction to
   * ensure that the audio context is not blocked by the browser.
   *
   * If an existing audio recorder is provided, it will be re-initialized with
   * the new audio stream to record instead of creating a new recorder.
   */
  const setUpRecording = useCallback(
    async ({ audioRecorder: givenAudioRecorder }: { audioRecorder?: AudioRecorder } = {}) => {
      const audioRecordingEntities = getAudioRecordingEntities();
      const isAlreadySetUp = audioRecordingEntities && !givenAudioRecorder;

      if (isAlreadySetUp) return audioRecordingEntities;

      const audioContext = new AudioContext();
      const audioDestinationNode = audioContext.createMediaStreamDestination();
      const audioStreamToRecord = audioDestinationNode.stream;

      const audioRecorder =
        (await givenAudioRecorder?.reinitMediaRecorder({
          sourceStream: audioStreamToRecord,
          shouldStartRecording: true,
        })) ?? createAudioRecorder({ audioStreamToRecord });

      sendLoggingEvents(TrackingEvents.SETTING_UP_AUDIO_RECORDING_ENV, {
        logLevel: 'info',
        message: `Setting up audio recording environment${givenAudioRecorder ? ' for existing recorder' : ''}`,
      });
      const audioEntities = {
        audioContext,
        audioDestinationNode,
        audioRecorder,
      };
      setAudioRecordingEntities(audioEntities);
      return audioEntities;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /**
   * Clears the audio recording entities
   */
  const tearDownRecording = useCallback(
    async () => {
      const audioRecorder = getAudioRecordingEntities()?.audioRecorder;
      if (!audioRecorder) return;

      sendLoggingEvents(
        TrackingEvents.TEARING_DOWN_AUDIO_RECORDING_ENV,
        {},
        {
          logLevel: 'info',
          message: 'Tearing down audio recording environment',
        },
      );
      setAudioRecordingEntities(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return {
    setUpRecording,
    tearDownRecording,
  };
}
